import PropTypes from "prop-types";
import { path } from "ramda";
import React, { useState, useEffect, useCallback } from "react";
import { Alert } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import apiMessages from "../../../lib/apiMessages.js";
import genericTranslations from "../../../utils/genericTranslations.js";
import { isLoadingDeviceStatus } from "../selectors.js";
import {
  fetchTabletRegistrationStatusById,
  fetchTabletRegistrationSuggestions,
  registerTablet,
} from "../thunks.js";

import ConfirmRegistrationModal from "./ConfirmRegistrationModal.jsx";
import UserSelection from "./UserSelection.jsx";

const TabletRegistration = () => {
  const intl = useIntl();
  const { identifier } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [error, setError] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  const [loadingRegistration, setLoadingRegistration] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [message, setMessage] = useState("");
  const [registrationSucceeded, setRegistrationSucceeded] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [users, setUsers] = useState([]);

  const loadingDeviceStatus = useSelector((state) =>
    isLoadingDeviceStatus(state, { identifier }),
  );
  useEffect(() => {
    const fetchData = async () => {
      let response;
      try {
        response = await dispatch(
          fetchTabletRegistrationStatusById(identifier),
        );
      } catch (err) {
        setError(err);
      }
      const status = path(["status"], response);
      const canReg = status === apiMessages.MSG_SUCCESS;
      const msg = path(["message"], response);
      setCanRegister(canReg);
      setMessage(msg);
    };

    if (identifier) {
      fetchData();
    }
  }, [identifier]);

  const onChangeNameInputHandler = useCallback((value) => {
    if (value.length < 3) {
      setUsers([]);
      return;
    }
    setLoadingUsers(true);
    setSelectedUser(null);

    let response;
    try {
      response = dispatch(fetchTabletRegistrationSuggestions(value));
    } catch (err) {
      setError(err);
    } finally {
      setLoadingUsers(false);
    }

    const userSuggestions = path(["User"], response);
    setUsers(userSuggestions || []);
  }, []);

  const onSelectUserHandler = useCallback((user) => {
    setSelectedUser(user);
    setShowRegistrationModal(true);
  }, []);

  const onExecuteRegistrationHandler = useCallback(async () => {
    setLoadingRegistration(true);

    const body = { identifier, userId: selectedUser.personnelNumber };
    let response;
    try {
      response = await dispatch(registerTablet(body));
    } catch (err) {
      setRegistrationSucceeded(false);
      setError(`Er ging iets fout bij het opslaan: ${err}`);
      setShowRegistrationModal(false);
    } finally {
      setLoadingRegistration(false);
    }
    setRegistrationSucceeded(true);
    setMessage(response.data.message);
    setShowRegistrationModal(false);
  }, [identifier, selectedUser]);

  const onCloseModalHandler = useCallback(() => {
    setShowRegistrationModal(false);
  }, []);

  // Redirect if registration succeeded.
  useEffect(() => {
    if (registrationSucceeded) {
      navigate(`/tablet-registration/${identifier}`);
    }
  }, [registrationSucceeded, navigate, identifier]);

  const dismissError = () => setError(null);

  if (error) {
    return (
      <Alert variant="danger" onClose={dismissError} dismissible>
        <Alert.Heading>
          {intl.formatMessage(genericTranslations.errorMessage)}
        </Alert.Heading>
        <p>{error.toString()}</p>
      </Alert>
    );
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        Device uitgeven
      </Card.Header>
      <Card.Body>
        {loadingDeviceStatus && (
          <span className="align-middle">
            Gegevens worden opgehaald voor CI-nummer {identifier}...
          </span>
        )}
        {registrationSucceeded && (
          <>
            <p>
              Het CI-nummer <code>{identifier}</code> is met succes gekoppeld
              aan gebruiker {selectedUser && selectedUser.displayName}!
            </p>
            {message && (
              <div className="well">
                <p>{message}</p>
              </div>
            )}
          </>
        )}
        {canRegister && registrationSucceeded === false && (
          <>
            <p>Registratie is mislukt. De opgegeven reden is:</p>
            <div className="well">
              <p>{message}</p>
            </div>
          </>
        )}
        {!canRegister && (
          <>
            <p>Registratie is niet mogelijk. De opgegeven reden is:</p>
            <div className="well">
              <p>{message}</p>
            </div>
          </>
        )}
        {canRegister && (
          <>
            <h5>{message}</h5>
            <p>
              Zoek een medewerker om te koppelen met CI-nummer{" "}
              <code>{identifier}</code>.
            </p>
            <UserSelection
              changeInput={onChangeNameInputHandler}
              loading={loadingUsers}
              selectUser={onSelectUserHandler}
              users={users}
            />
            <ConfirmRegistrationModal
              close={onCloseModalHandler}
              identifier={identifier}
              loading={loadingRegistration}
              register={onExecuteRegistrationHandler}
              showRegistrationModal={showRegistrationModal}
              user={selectedUser}
            />
          </>
        )}
      </Card.Body>
    </Card>
  );
};
TabletRegistration.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
  }).isRequired,
  get: PropTypes.func.isRequired,
  post: PropTypes.func.isRequired,
};

export default TabletRegistration;
